// Pieza 1: el camino (diseño 4a, §2.3). Tu cliente a la izquierda, los intermediarios en medio y los sancionados a la derecha, en
// carriles horizontales: un carril por camino (un hallazgo con a_traves_de) o por episodio (un hallazgo directo). La pieza no tiene
// mapa propio de dirección: la punta sigue al `sentido` que calcula la base desde el acto (§2.0.2), y la flecha sale de quien tiene
// el papel. Todo texto sale del documento o de ROTULOS.
import type { EstadoLista, Fuente, Hallazgo, Hallazgos, Sentido } from '../hallazgos/tipos.ts';
import type { Caja, Dibujo, Elemento, Tinta } from './dibujo.ts';
import { abrible, seAbre } from './fuente.ts';
import { acomodar, BORDE, medir, type Peso, SEPARACION, sinRegimen } from './medida.ts';
import { numeroLlano, ROTULOS, rotulo, SELLO_IDENTIDAD, SELLO_LISTA } from './rotulos.ts';

/** Tamaños de letra (px) y pesos. */
const T = {
  nombre: { tam: 13, peso: 600 as Peso },
  intermedio: { tam: 12, peso: 600 as Peso },
  papel: { tam: 12, peso: 500 as Peso },
  detalle: { tam: 11, peso: 400 as Peso },
  sello: { tam: 10, peso: 700 as Peso },
  marca: { tam: 10, peso: 400 as Peso },
  mas: { tam: 11, peso: 500 as Peso },
};
/** Relleno dentro de una figura y dentro de un sello (px). */
const DENTRO = 6;
const DENTRO_SELLO = 4;
/** Aire entre el rótulo de una flecha y su línea, y arriba y abajo de cada carril (px). */
const SOBRE_LINEA = 3;
const AIRE = 6;

/** Un tramo dibujado: la línea entre dos columnas y su rótulo. */
interface Tramo {
  papel: string;
  sentido: Sentido;
  /** undefined: el tramo no dice fuente; null: la base no la tiene. */
  fuente: Fuente | null | undefined;
  porcentaje: number | null;
  acciones: number | null;
  total: number | null;
}

interface Figura {
  clave: string;
  nombre: string;
  /** Sellos de la figura: texto, rojo o gris, discontinuo, y fuente que abre. */
  sellos: { texto: string; rojo: boolean; discontinuo: boolean; fuente: Fuente | null; argus: boolean }[];
  rojo: boolean;
  fuente: Fuente | null;
}

interface Carril {
  hallazgo: Hallazgo;
  /** Índice del carril dentro del hallazgo. */
  k: number;
  intermedios: Figura[];
  tramos: Tramo[];
}

interface Grupo {
  ref: string;
  figura: Figura;
  carriles: Carril[];
  peso: number;
}

/** Tramos de un hallazgo: los de a_traves_de y su siguiente si los hay; si no, uno por episodio; si no, la relación que declara OFAC. */
export function tramosDe(h: Hallazgo): { intermedios: Hallazgo['relacion']['a_traves_de']; tramos: Tramo[] }[] {
  const via = h.relacion.a_traves_de;
  if (via.length > 0) {
    const e0 = h.episodios[0];
    const tramos: Tramo[] = via.map((p, i) => ({
      papel: p.papel_texto,
      sentido: p.sentido,
      // El primer tramo es el de episodios[0]: su fuente y su porcentaje van ahí (contrato, pasoIntermedio.fuente).
      fuente: i === 0 ? e0?.fuente : p.fuente,
      porcentaje: i === 0 ? e0?.porcentaje ?? null : null,
      acciones: i === 0 ? e0?.acciones ?? null : null,
      total: i === 0 ? e0?.total_acciones ?? null : null,
    }));
    const ultimo = via[via.length - 1]!.siguiente;
    if (ultimo) {
      tramos.push({ papel: ultimo.papel_texto, sentido: ultimo.sentido, fuente: ultimo.fuente, porcentaje: null, acciones: null, total: null });
    }
    return [{ intermedios: via, tramos }];
  }
  if (h.episodios.length > 0) {
    return h.episodios.map((e) => ({
      intermedios: [],
      tramos: [{ papel: e.papel_texto, sentido: e.sentido, fuente: e.fuente, porcentaje: e.porcentaje, acciones: e.acciones, total: e.total_acciones }],
    }));
  }
  // co y segun_ofac: OFAC declara la relación; no hay acto, así que no hay punta (I2). La flecha lleva relacion.texto.
  const fuente = h.frases.find((f) => f.fuente)?.fuente ?? null;
  return [{ intermedios: [], tramos: [{ papel: h.relacion.texto, sentido: 'mutuo', fuente, porcentaje: null, acciones: null, total: null }] }];
}

/** Los sellos del estado en la lista; el de «cuenta como sancionada» es una deducción de Argus (P5, regla pareja I-m). */
function sellosDeLista(estado: EstadoLista, fuenteLista: Fuente | null): Figura['sellos'] {
  const texto = SELLO_LISTA[estado.estado];
  if (!texto) return [];
  if (estado.estado === 'cuenta_como_sancionada') {
    const pct = estado.cuenta?.porcentaje;
    return [{
      texto: pct != null ? `${texto} · ${rotulo(ROTULOS.porcentaje, { n: numeroLlano(pct) })}` : texto,
      rojo: true,
      discontinuo: true,
      fuente: estado.cuenta?.fuente ?? null,
      argus: true,
    }];
  }
  return [{
    texto,
    rojo: estado.estado === 'en_lista',
    discontinuo: estado.estado === 'posible',
    fuente: estado.estado === 'en_lista' || estado.estado === 'retirada' ? fuenteLista : null,
    argus: false,
  }];
}

/** La figura de un sancionado: rojo sólo si su identidad está confirmada (o la declara OFAC) y está en la lista. */
function figuraContraparte(doc: Hallazgos, h: Hallazgo): Figura {
  const s = doc.sancionados[h.contraparte.ref];
  const identidad = h.contraparte.identidad;
  const fuenteLista = s?.fuente ?? null;
  let sellos: Figura['sellos'];
  if (identidad && identidad.nivel !== 'confirmada') {
    const texto = SELLO_IDENTIDAD[identidad.nivel]!;
    sellos = [{ texto, rojo: false, discontinuo: identidad.nivel !== 'descartada', fuente: null, argus: false }];
  } else sellos = s ? sellosDeLista(s.en_lista, fuenteLista) : [];
  return { clave: h.contraparte.ref, nombre: h.contraparte.nombre, sellos, rojo: sellos.some((x) => x.rojo), fuente: fuenteLista };
}

function figuraIntermedio(p: Hallazgo['relacion']['a_traves_de'][number]): Figura {
  const sellos = sellosDeLista(p.en_lista, null);
  return { clave: p.ref, nombre: p.nombre, sellos, rojo: sellos.some((x) => x.rojo), fuente: null };
}

/** Lo que dice la flecha además del papel: «12 %» o «N acciones, no sabemos el total», y «acta [año]». */
function detalle(t: Tramo): string[] {
  const partes: string[] = [];
  if (t.porcentaje != null) partes.push(rotulo(ROTULOS.porcentaje, { n: numeroLlano(t.porcentaje) }));
  else if (t.acciones != null && t.total == null) partes.push(rotulo(ROTULOS.accionesSinTotal, { n: numeroLlano(t.acciones) }));
  const anio = t.fuente?.tipo === 'acta' && t.fuente.fecha ? t.fuente.fecha.slice(0, 4) : null;
  if (anio) partes.push(rotulo(ROTULOS.acta, { anio }));
  return partes;
}

/** Une las partes del detalle con « · » mientras quepan en una línea; una parte nunca se parte entre líneas. */
function porPartes(partes: string[], ancho: number, tam: number, peso: Peso): string[] {
  const lineas: string[] = [];
  for (const p of partes) {
    const ultima = lineas.at(-1);
    if (ultima !== undefined && medir(`${ultima} · ${p}`, tam, peso).w <= ancho) lineas[lineas.length - 1] = `${ultima} · ${p}`;
    else lineas.push(acomodar(p, ancho, 1, tam, peso).lineas[0]!);
  }
  return lineas;
}

/** Los grupos (uno por sancionado, ordenados por peso) con sus carriles. */
function grupos(doc: Hallazgos): Grupo[] {
  const porRef = new Map<string, Grupo>();
  for (const h of [...doc.hallazgos].sort((a, b) => a.peso - b.peso)) {
    let g = porRef.get(h.contraparte.ref);
    if (!g) {
      g = { ref: h.contraparte.ref, figura: figuraContraparte(doc, h), carriles: [], peso: h.peso };
      porRef.set(h.contraparte.ref, g);
    }
    tramosDe(h).forEach((c, k) => g!.carriles.push({ hallazgo: h, k, intermedios: c.intermedios.map(figuraIntermedio), tramos: c.tramos }));
  }
  return [...porRef.values()];
}


/** Arma el dibujo de texto de una figura: nombre partido, sellos y la marca «Lo deduce Argus». Devuelve su alto. */
interface Contenido {
  alto: number;
  pintar: (id: string, caja: Caja, tintaNombre: Tinta) => Elemento[];
}

export interface OpcionesCamino {
  ancho: number;
  maxFiguras?: number;
}

/** El camino de una ficha; null si no hay hallazgos. */
export function camino(doc: Hallazgos, op: OpcionesCamino): Dibujo | null {
  if (doc.hallazgos.length === 0) return null;
  const ancho = Math.max(240, Math.round(op.ancho));
  const max = op.maxFiguras ?? 7;
  const todos = grupos(doc);

  // Siete figuras como máximo, contando a tu cliente, también en el primer grupo. Se decide por relación (hallazgo), en orden de
  // peso: entra si caben su sancionado (si es el primero de su grupo) y sus intermediarios. «+n relaciones más» cuenta las relaciones
  // que no entraron, que sí están en la lista de abajo (decisión de Bernardo, 2026-10-04).
  const dibujados: Grupo[] = [];
  let usadas = 1;
  let omitidos = 0;
  for (const g of todos) {
    const porHallazgo = new Map<string, Carril[]>();
    for (const c of g.carriles) porHallazgo.set(c.hallazgo.id, [...(porHallazgo.get(c.hallazgo.id) ?? []), c]);
    const elegidos: Carril[] = [];
    for (const cs of porHallazgo.values()) {
      const cuesta = (elegidos.length ? 0 : 1) + cs.reduce((n, c) => n + c.intermedios.length, 0);
      // Si todavía no hay nada dibujado, la primera relación entra aunque pase del tope: el dibujo nunca queda vacío.
      if (usadas + cuesta > max && (usadas > 1 || dibujados.length > 0 || elegidos.length > 0)) {
        omitidos++;
        continue;
      }
      elegidos.push(...cs);
      usadas += cuesta;
    }
    if (elegidos.length) dibujados.push({ ...g, carriles: elegidos });
  }

  // Columnas: tu cliente, K intermediarios, sancionados. Las columnas de figuras y los huecos para los rótulos se reparten parejo.
  const K = Math.max(0, ...dibujados.flatMap((g) => g.carriles.map((c) => c.intermedios.length)));
  const ncol = K + 2;
  const util = ancho - 2 * BORDE;
  // Anchos: primero lo que pide la palabra más larga de cada columna (nombres y sellos) y de cada hueco (papeles), para no partir
  // palabras; lo que sobra se reparte (las columnas de figuras pesan 1.3 y los huecos 1). Si no alcanza, todo se encoge parejo.
  const palabraMasLarga = (t: string, tam: number, peso: Peso) =>
    Math.max(0, ...sinRegimen(t).split(' ').filter(Boolean).map((p) => medir(p, tam, peso).w));
  const pideFigura = (f: Figura, letra: { tam: number; peso: Peso }) =>
    2 * DENTRO + Math.max(palabraMasLarga(f.nombre, letra.tam, letra.peso), ...f.sellos.map((x) => palabraMasLarga(x.texto, T.sello.tam, T.sello.peso) + 2 * DENTRO_SELLO));
  const pide: number[] = new Array(2 * ncol - 1).fill(0);
  pide[0] = pideFigura({ clave: '', nombre: doc.sujeto.nombre, sellos: sellosDeLista(doc.en_lista, null), rojo: false, fuente: null }, T.nombre);
  for (const g of dibujados) {
    pide[2 * (K + 1)] = Math.max(pide[2 * (K + 1)]!, pideFigura(g.figura, T.nombre));
    for (const c of g.carriles) {
      c.intermedios.forEach((f, i) => (pide[2 * (i + 1)] = Math.max(pide[2 * (i + 1)]!, pideFigura(f, T.intermedio))));
      const cols = [0, ...c.intermedios.map((_, i) => i + 1), K + 1];
      c.tramos.forEach((t, i) => {
        if (cols[i + 1]! - cols[i]! !== 1) return; // un tramo que cruza columnas vacías tiene más lugar
        const hueco = 2 * cols[i]! + 1;
        const det = Math.max(0, ...detalle(t).map((d) => medir(d, T.detalle.tam, T.detalle.peso).w));
        pide[hueco] = Math.max(pide[hueco]!, Math.max(palabraMasLarga(t.papel, T.papel.tam, T.papel.peso), det) + 2 * SEPARACION);
      });
    }
  }
  const total = pide.reduce((a, b) => a + b, 0);
  const pesos = pide.map((_, i) => (i % 2 === 0 ? 1.3 : 1));
  const sumaPesos = pesos.reduce((a, b) => a + b, 0);
  const anchos = total <= util
    ? pide.map((p, i) => p + ((util - total) * pesos[i]!) / sumaPesos)
    : pide.map((p, i) => (p * 0.5 + (util * 0.5 * pesos[i]!) / sumaPesos) * (util / (total * 0.5 + util * 0.5)));
  const inicio: number[] = [];
  anchos.reduce((x, w, i) => (inicio[i] = x, x + w), BORDE);
  const colX = (i: number) => inicio[2 * i]!;
  const colW = (i: number) => anchos[2 * i]!;

  const fuentes: Fuente[] = [];
  const indice = new Map<string, number>();
  const fuenteIdx = (f: Fuente | null | undefined): number | undefined => {
    if (!f) return undefined;
    // Corte 0: una fuente que no se abre (un tipo que la pantalla no conoce) no entra al dibujo, así el elemento no es un botón que no
    // abre nada. Que no se abra depende sólo del tipo, no del contexto.
    if (!seAbre(abrible(f, { corrida: null }))) return undefined;
    const k = JSON.stringify(f);
    if (!indice.has(k)) {
      indice.set(k, fuentes.length);
      fuentes.push(f);
    }
    return indice.get(k);
  };

  /** Prepara el contenido de una figura de ancho w. */
  const contenido = (f: Figura, w: number, letra: { tam: number; peso: Peso }, lineasNombre: number): Contenido => {
    const interior = w - 2 * DENTRO;
    const nombre = acomodar(f.nombre, interior, lineasNombre, letra.tam, letra.peso);
    const altoLinea = medir('Ag', letra.tam, letra.peso).h;
    const sellos = f.sellos.map((s) => {
      const lineas = acomodar(s.texto, interior - 2 * DENTRO_SELLO, 3, T.sello.tam, T.sello.peso).lineas;
      const marca = s.argus ? acomodar(ROTULOS.leyendaArgus, interior, 2, T.marca.tam, T.marca.peso).lineas : [];
      return { s, lineas, marca };
    });
    const altoSello = medir('Ag', T.sello.tam, T.sello.peso).h;
    const altoMarca = medir('Ag', T.marca.tam, T.marca.peso).h;
    const alto = 2 * DENTRO + nombre.lineas.length * altoLinea +
      sellos.reduce((n, x) => n + SEPARACION + x.lineas.length * altoSello + 2 * DENTRO_SELLO + x.marca.length * altoMarca, 0);
    const pintar = (id: string, caja: Caja, tintaNombre: Tinta): Elemento[] => {
      const els: Elemento[] = [];
      let y = caja.y + (caja.h - alto) / 2 + DENTRO;
      const cx = caja.x + caja.w / 2;
      for (const [i, l] of nombre.lineas.entries()) {
        const m = medir(l, letra.tam, letra.peso);
        els.push({
          tipo: 'texto', id: `${id}:nombre${i}`, x: cx, y: y + m.arriba, ancla: 'medio', tam: letra.tam, peso: letra.peso, tinta: tintaNombre,
          contenido: l, completo: f.nombre, origen: 'documento', caja: { x: cx - m.w / 2, y, w: m.w, h: m.h }, duenio: id,
        });
        y += altoLinea;
      }
      for (const [j, x] of sellos.entries()) {
        y += SEPARACION;
        const anchoS = Math.max(...x.lineas.map((l) => medir(l, T.sello.tam, T.sello.peso).w)) + 2 * DENTRO_SELLO;
        const sid = `${id}/sello${j}`;
        const tinta: Tinta = x.s.rojo ? 'rojo' : 'gris';
        const hS = x.lineas.length * altoSello + 2 * DENTRO_SELLO;
        els.push({
          tipo: 'rect', id: sid, caja: { x: cx - anchoS / 2, y, w: anchoS, h: hS }, radio: 3, relleno: 'blanco', borde: tinta,
          ...(x.s.discontinuo ? { discontinuo: true } : {}), ...(fuenteIdx(x.s.fuente) !== undefined ? { fuente: fuenteIdx(x.s.fuente)! } : {}),
          ...(x.s.fuente?.tipo === 'lista_ofac' && f.clave ? { sancionado: f.clave } : {}),
        });
        let ys = y + DENTRO_SELLO;
        for (const [i, l] of x.lineas.entries()) {
          const m = medir(l, T.sello.tam, T.sello.peso);
          els.push({
            tipo: 'texto', id: `${sid}:texto${i}`, x: cx, y: ys + m.arriba, ancla: 'medio', tam: T.sello.tam, peso: T.sello.peso, tinta,
            contenido: l, completo: x.s.texto, origen: 'rotulo', caja: { x: cx - m.w / 2, y: ys, w: m.w, h: m.h }, duenio: sid,
          });
          ys += altoSello;
        }
        y += hS;
        for (const [i, l] of x.marca.entries()) {
          const m = medir(l, T.marca.tam, T.marca.peso);
          els.push({
            tipo: 'texto', id: `${sid}:argus${i}`, x: cx, y: y + m.arriba, ancla: 'medio', tam: T.marca.tam, peso: T.marca.peso, tinta: 'tenue',
            contenido: l, completo: ROTULOS.leyendaArgus, origen: 'rotulo', caja: { x: cx - m.w / 2, y, w: m.w, h: m.h }, duenio: id,
          });
          y += altoMarca;
        }
      }
      return els;
    };
    return { alto, pintar };
  };

  // Carriles en orden: por sancionado (peso) y, dentro, por hallazgo y episodio.
  const carriles = dibujados.flatMap((g) => g.carriles.map((c) => ({ g, c })));
  type Rotulo = { lineas: { texto: string; completo: string; origen: 'documento' | 'rotulo'; tam: number; peso: Peso; tinta: Tinta }[]; alto: number };
  const planes = carriles.map(({ c }) => {
    // Segmentos: de la columna a a la b. Los intermediarios ocupan las primeras columnas; el último tramo llega hasta los sancionados.
    const cols = [0, ...c.intermedios.map((_, i) => i + 1), K + 1];
    const segmentos = c.tramos.map((t, i) => {
      const a = cols[i]!, b = cols[i + 1]!;
      const wSeg = colX(b) - (colX(a) + colW(a)) - 2 * SEPARACION;
      const papel = acomodar(t.papel, wSeg, 4, T.papel.tam, T.papel.peso);
      const det = detalle(t);
      const lineasDet = porPartes(det, wSeg, T.detalle.tam, T.detalle.peso);
      const lineas: Rotulo['lineas'] = [
        ...papel.lineas.map((l) => ({ texto: l, completo: t.papel, origen: 'documento' as const, ...T.papel, tinta: 'tinta' as Tinta })),
        ...lineasDet.map((l) => ({ texto: l, completo: det.join(' · '), origen: 'rotulo' as const, ...T.detalle, tinta: 'tenue' as Tinta })),
      ];
      const alto = lineas.reduce((n, l) => n + medir('Ag', l.tam, l.peso).h, 0);
      return { t, a, b, wSeg, rotulo: { lineas, alto } as Rotulo };
    });
    const intermedios = c.intermedios.map((f, i) => ({ f, col: i + 1, cont: contenido(f, colW(i + 1), T.intermedio, 3) }));
    const arribaRotulos = Math.max(0, ...segmentos.map((s) => s.rotulo.alto + SOBRE_LINEA));
    const mitadFiguras = Math.max(0, ...intermedios.map((x) => x.cont.alto / 2));
    return { segmentos, intermedios, arriba: Math.max(arribaRotulos, mitadFiguras) + AIRE, abajo: Math.max(mitadFiguras, 8) + AIRE };
  });

  // Figuras que abarcan varios carriles: tu cliente (todos) y cada sancionado (los suyos). Si no caben, los carriles crecen.
  const sujetoFigura: Figura = {
    clave: doc.sujeto.ref,
    // El cliente con su nombre completo, no el corto de desambiguación (Bernardo, 2026-10-04, al aprobar las capturas).
    nombre: doc.sujeto.nombre,
    sellos: sellosDeLista(doc.en_lista, doc.sancionados[doc.sujeto.ref]?.fuente ?? null),
    rojo: false,
    fuente: null,
  };
  const contSujeto = contenido(sujetoFigura, colW(0), T.nombre, 4);
  const contGrupos = dibujados.map((g) => contenido(g.figura, colW(K + 1), T.nombre, 4));
  const altos = planes.map((p) => p.arriba + p.abajo);
  const crecer = (desde: number, hasta: number, necesita: number) => {
    const tiene = altos.slice(desde, hasta + 1).reduce((a, b) => a + b, 0);
    if (necesita > tiene) altos[hasta]! += necesita - tiene;
  };
  let i0 = 0;
  for (const [gi, g] of dibujados.entries()) {
    const n = g.carriles.length;
    crecer(i0, i0 + n - 1, contGrupos[gi]!.alto + 2 * AIRE);
    i0 += n;
  }
  crecer(0, altos.length - 1, contSujeto.alto + 2 * AIRE);

  // «+n relaciones más»: un carril más abajo, en la columna de los sancionados.
  const masTexto = omitidos > 0 ? rotulo(omitidos === 1 ? ROTULOS.masRelacion : ROTULOS.masRelaciones, { n: omitidos }) : null;
  const masLineas = masTexto ? acomodar(masTexto, colW(K + 1) - 2 * DENTRO, 2, T.mas.tam, T.mas.peso).lineas : [];
  const altoMas = masLineas.length ? masLineas.length * medir('Ag', T.mas.tam, T.mas.peso).h + 2 * DENTRO + 2 * AIRE : 0;

  const tops: number[] = [];
  let y = BORDE;
  for (const a of altos) {
    tops.push(y);
    y += a;
  }
  const alto = Math.ceil(y + altoMas + BORDE);
  const lineaY = (i: number) => tops[i]! + planes[i]!.arriba + (altos[i]! - planes[i]!.arriba - planes[i]!.abajo) / 2;

  const figuras: Elemento[] = [];
  const lineas: Elemento[] = [];
  const textos: Elemento[] = [];
  const caja = (col: number, y0: number, y1: number): Caja => ({ x: colX(col), y: y0, w: colW(col), h: y1 - y0 });

  // Tu cliente.
  {
    const c = caja(0, tops[0]! + AIRE, y - AIRE);
    figuras.push({ tipo: 'rect', id: 'cliente', caja: c, radio: doc.sujeto.tipo === 'persona' ? 14 : 6, relleno: 'clienteFondo', borde: 'cliente' });
    textos.push(...contSujeto.pintar('cliente', c, 'cliente'));
  }
  // Sancionados.
  i0 = 0;
  for (const [gi, g] of dibujados.entries()) {
    const n = g.carriles.length;
    const y0 = tops[i0]! + AIRE, y1 = tops[i0 + n - 1]! + altos[i0 + n - 1]! - AIRE;
    const id = `san${gi}`;
    const c = caja(K + 1, y0, y1);
    const persona = doc.sancionados[g.ref]?.tipo === 'persona';
    const fi = fuenteIdx(g.figura.fuente);
    figuras.push({
      tipo: 'rect', id, caja: c, radio: persona ? 14 : 6, relleno: g.figura.rojo ? 'rojoFondo' : 'grisFondo', borde: g.figura.rojo ? 'rojo' : 'gris',
      ...(fi !== undefined ? { fuente: fi, sancionado: g.ref } : {}),
    });
    textos.push(...contGrupos[gi]!.pintar(id, c, g.figura.rojo ? 'rojo' : 'tinta'));
    i0 += n;
  }
  // Carriles: intermediarios, líneas y rótulos.
  for (const [ci, { c }] of carriles.entries()) {
    const plan = planes[ci]!;
    const ly = lineaY(ci);
    for (const [ii, x] of plan.intermedios.entries()) {
      const id = `int:${c.hallazgo.id}:${c.k}:${ii}`;
      const cajaI = { x: colX(x.col), y: ly - x.cont.alto / 2, w: colW(x.col), h: x.cont.alto };
      figuras.push({ tipo: 'rect', id, caja: cajaI, radio: 6, relleno: x.f.rojo ? 'rojoFondo' : 'grisFondo', borde: x.f.rojo ? 'rojo' : 'gris' });
      textos.push(...x.cont.pintar(id, cajaI, x.f.rojo ? 'rojo' : 'tinta'));
    }
    for (const [si, s] of plan.segmentos.entries()) {
      const id = `l:${c.hallazgo.id}:${c.k}:${si}`;
      const xa = colX(s.a) + colW(s.a), xb = colX(s.b);
      const conPunta = s.t.sentido === 'sale' || s.t.sentido === 'llega';
      const puntos: [number, number][] = s.t.sentido === 'llega' ? [[xb, ly], [xa, ly]] : [[xa, ly], [xb, ly]];
      const fi = fuenteIdx(s.t.fuente);
      lineas.push({
        tipo: 'linea', id, puntos, borde: 'tenue', grosor: 1.5, discontinua: false, flecha: conPunta ? 'fin' : 'ninguna',
        ...(fi !== undefined ? { fuente: fi, sancionado: c.hallazgo.contraparte.ref } : {}),
      });
      // El rótulo, encima de la línea y centrado en su tramo.
      const cx = (xa + xb) / 2;
      let yr = ly - SOBRE_LINEA - s.rotulo.alto;
      for (const [li, l] of s.rotulo.lineas.entries()) {
        const m = medir(l.texto, l.tam, l.peso);
        textos.push({
          tipo: 'texto', id: `${id}:rotulo${li}`, x: cx, y: yr + m.arriba, ancla: 'medio', tam: l.tam, peso: l.peso, tinta: l.tinta, contenido: l.texto,
          completo: l.completo, origen: l.origen, caja: { x: cx - m.w / 2, y: yr, w: m.w, h: m.h }, duenio: id,
          ...(fi !== undefined ? { fuente: fi, sancionado: c.hallazgo.contraparte.ref } : {}),
        });
        yr += medir('Ag', l.tam, l.peso).h;
      }
    }
  }
  if (masTexto) {
    const altoL = medir('Ag', T.mas.tam, T.mas.peso).h;
    const h = masLineas.length * altoL + 2 * DENTRO;
    const c = { x: colX(K + 1), y: y + AIRE, w: colW(K + 1), h };
    figuras.push({ tipo: 'rect', id: 'mas', caja: c, radio: h / 2, relleno: 'blanco', borde: 'gris' });
    let ym = c.y + DENTRO;
    for (const [i, l] of masLineas.entries()) {
      const m = medir(l, T.mas.tam, T.mas.peso);
      textos.push({
        tipo: 'texto', id: `mas:texto${i}`, x: c.x + c.w / 2, y: ym + m.arriba, ancla: 'medio', tam: T.mas.tam, peso: T.mas.peso, tinta: 'gris',
        contenido: l, completo: masTexto, origen: 'rotulo', caja: { x: c.x + c.w / 2 - m.w / 2, y: ym, w: m.w, h: m.h }, duenio: 'mas',
      });
      ym += altoL;
    }
  }

  const frases = [...new Set(dibujados.flatMap((g) => g.carriles.map((c) => c.hallazgo.relacion.frase)))];
  return { ancho, alto, descripcion: frases.join(' '), elementos: [...figuras, ...lineas, ...textos], fuentes, omitidos };
}

/** Lo que va debajo del dibujo (17b y 17c): cada conteo de `omitido` dicho aparte. */
export function avisosOmitidos(doc: Hallazgos): string[] {
  const avisos: string[] = [];
  const { hallazgos: h, relaciones_lejanas: l } = doc.omitido;
  if (h > 0) avisos.push(rotulo(h === 1 ? ROTULOS.omitidoHallazgo : ROTULOS.omitidosHallazgos, { n: h }));
  if (l > 0) avisos.push(rotulo(l === 1 ? ROTULOS.omitidaLejana : ROTULOS.omitidosLejanas, { n: l }));
  return avisos;
}

/** Una parte de una cadena en miniatura: una figura o un tramo con su flecha de texto. */
export type ParteCadena =
  | { tipo: 'figura'; texto: string; rojo: boolean }
  | { tipo: 'tramo'; papel: string; flecha: '→' | '←' | '—' };
export interface Cadena {
  hallazgo: string;
  partes: ParteCadena[];
}

const FLECHA: Record<Sentido, '→' | '←' | '—'> = { sale: '→', llega: '←', mutuo: '—', desconocido: '—' };

/** Cadenas en miniatura para el buscador: las de los `max` hallazgos de más peso, como datos que la SPA pinta en línea. */
export function caminoMiniatura(doc: Hallazgos, max = 2): Cadena[] {
  return [...doc.hallazgos].sort((a, b) => a.peso - b.peso).slice(0, max).map((h) => {
    const c = tramosDe(h)[0]!;
    const destino = figuraContraparte(doc, h);
    const partes: ParteCadena[] = [{ tipo: 'figura', texto: doc.sujeto.nombre, rojo: false }];
    c.tramos.forEach((t, i) => {
      partes.push({ tipo: 'tramo', papel: t.papel, flecha: FLECHA[t.sentido] });
      const inter = c.intermedios[i];
      if (inter) partes.push({ tipo: 'figura', texto: inter.nombre, rojo: figuraIntermedio(inter).rojo });
    });
    partes.push({ tipo: 'figura', texto: h.contraparte.nombre, rojo: destino.rojo });
    return { hallazgo: h.id, partes };
  });
}
